import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSpring } from 'react-spring';
import { useTheme } from 'styled-components';
import Tooltip from './Tooltip';
import * as Styled from './Button.style';
import { day } from 'theme';
import Admin from 'admin';

const cooldownSeconds = 1;

const ButtonCooldown = ({ cooldown, level }) => {
  const theme = useTheme();
  const maxLevel = 10;
  // Calculate intensity based on level. The base comes from the palette so the sweep stays a
  // shade against the button rather than a bright flash at night.
  const base = theme?.cooldown ?? day.cooldown;
  const intensityReduction = Math.pow(level / maxLevel, 2);
  const intensity = Math.round(base - base * intensityReduction);
  const bgColor = `rgb(${intensity}, ${intensity}, ${intensity})`;

  const [style] = useSpring(() => ({
    background: `conic-gradient(transparent 100%, ${bgColor}  0 100%)`,
    from: {
      background: `conic-gradient(transparent 1%, ${bgColor} 0 100%)`
    },
    config: { duration: (cooldown || 0.2) * 1000 }
  }));

  return <Styled.ButtonCooldown style={style} />;
};

const underlineHotkey = (text, hotkey) => {
  if (!hotkey) return text;
  const index = text.toLowerCase().indexOf(hotkey.toLowerCase());
  if (index === -1) return text; // No match found
  return (
    <>
      {text.substring(0, index)}
      <span style={{ textDecoration: 'underline' }}>{text[index]}</span>
      {text.substring(index + 1)}
    </>
  );
};

const Button = ({
  id = 'BTN_',
  text,
  onClick,
  forceDisabled = false,
  tooltips = [],
  cooldown = Admin().cooldown === null ? cooldownSeconds : Admin().cooldown,
  hotkey,
  isAccept,
  hasEvent,
  level = 0
}) => {
  const [isCoolingDown, setCoolingDown] = useState();
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (!disabled) return;

    const timeoutId = setTimeout(() => {
      setDisabled(false);
    }, cooldown * 1000);

    return () => clearTimeout(timeoutId);
  }, [disabled, cooldown]);

  const clicked = useCallback(() => {
    if (disabled || forceDisabled || !onClick) return;

    setCoolingDown(isCoolingDown => !isCoolingDown);
    setDisabled(true);
    onClick();
  }, [disabled, forceDisabled, onClick]);

  const handleKeyPress = useCallback(
    event => {
      if (disabled || forceDisabled || hasEvent) return;
      const hotkeyPressed = event.key.toLowerCase() === hotkey?.toLowerCase();
      const accepted = isAccept && (event.key === 'Enter' || event.key === ' ');
      if (hotkeyPressed || accepted) clicked();
    },
    [disabled, forceDisabled, hotkey, isAccept, clicked]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  return (
    <Styled.Button id={id} disabled={forceDisabled || disabled} onClick={clicked} hotkey={hotkey} level={level}>
      {underlineHotkey(text, hotkey)}
      {isCoolingDown !== undefined && (
        <ButtonCooldown key={isCoolingDown === false} cooldown={cooldown} level={level} />
      )}
      {tooltips.length > 0 && <Tooltip entries={tooltips} />}
    </Styled.Button>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  forceDisabled: PropTypes.bool,
  tooltips: PropTypes.array,
  cooldown: PropTypes.number,
  hotkey: PropTypes.string,
  isAccept: PropTypes.bool,
  level: PropTypes.number
};

export default Button;
